"use client";

import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const studentFeatures = [{
  id: 1,
  title: "Safety First",
  description: "Every journey is monitored in real-time by our dedicated security team to ensure your absolute peace of mind."
}, {
  id: 2,
  title: "Reliability",
  description: "Our vetted professional drivers and advanced dispatch system guarantee a ride is always there when you need it."
}, {
  id: 3,
  title: "Speed",
  description: "Optimized routing algorithms and a dense network of premium vehicles ensure the fastest arrival times in the city."
}, {
  id: 4,
  title: "24/7 Support",
  description: "A dedicated concierge support line is available around the clock to assist with any request or concern."
}, {
  id: 5,
  title: "Share My Trip",
  description: "Instantly share your live location and trip details with trusted contacts directly from the Moveonix app."
}, {
  id: 6,
  title: "Emergency Assist",
  description: "One-tap access to local emergency services and our internal rapid-response safety unit for immediate help."
}];
const adminFeatures = [{
  id: 1,
  title: "Fleet Oversight",
  description: "Comprehensive real-time monitoring of all active vehicles, ensuring adherence to our premium safety standards."
}, {
  id: 2,
  title: "Driver Vetting",
  description: "Rigorous multi-step background checks and performance reviews to maintain the elite Moveonix driver network."
}, {
  id: 3,
  title: "Incident Analytics",
  description: "Advanced data modeling to predict and prevent potential safety issues before they occur on the road."
}, {
  id: 4,
  title: "Corporate Controls",
  description: "Granular permission settings for business accounts to manage employee mobility and safety protocols."
}, {
  id: 5,
  title: "Global Compliance",
  description: "Automated systems ensuring all operations meet local regulatory requirements across our international markets."
}, {
  id: 6,
  title: "Secure Infrastructure",
  description: "Bank-grade encryption for all user data and transaction history, protecting your privacy at every turn."
}];
export default function ModuleCapabilities() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const tabsReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224342" id="the-gold-standard-of-safety" className="w-full py-24 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[500px] bg-primary/5 rounded-lg blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerReveal.ref} className={`flex flex-col items-center text-center mb-16 transition-all duration-700 ease-out ${headerReveal.isVisible ? "animate-slide-up opacity-100" : "opacity-0"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold font-default text-foreground mb-4 tracking-tight">
            The Gold Standard of Safety
          </h2>
          <p className="text-lg text-muted-foreground font-default max-w-2xl">
            Experience the pinnacle of secure transportation with our multi-layered safety ecosystem designed for the modern traveler.
          </p>
        </div>

        <div ref={tabsReveal.ref} className={`transition-all duration-700 delay-200 ease-out ${tabsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Tabs defaultValue="students" className="w-full flex flex-col items-center">
            <TabsList className="mb-12 grid w-full max-w-[400px] grid-cols-2 p-1 bg-muted/50 backdrop-blur-sm border border-border/50 rounded-md">
              <TabsTrigger value="students" className="font-default text-base rounded-md duration-300 hover:text-primary transition-colors">For Riders</TabsTrigger>
              <TabsTrigger value="admins" className="font-default text-base rounded-md duration-300 hover:text-primary transition-colors">For Partners</TabsTrigger>
            </TabsList>

            <TabsContent value="students" className="w-full mt-0 outline-none">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {studentFeatures.map((feature, i) => <Card key={feature.id} data-index={i} className={`bg-white border-border/50 shadow-sm hover:shadow-md hover:scale-[1.04] hover:border-primary/20 transition-all duration-300 group rounded-md animate-fade-in ${i % 3 === 0 ? "delay-100" : i % 3 === 1 ? "delay-200" : "delay-300"}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center size-8 rounded-md bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <div className="size-2.5 rounded-md bg-primary ring-4 ring-primary/20 group-hover:ring-primary/30 transition-all" />
                        </div>
                        <CardTitle className="font-default text-xl text-foreground">
                          {feature.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground font-default leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>)}
              </div>
            </TabsContent>

            <TabsContent value="admins" className="w-full mt-0 outline-none">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {adminFeatures.map((feature, i) => <Card key={feature.id} data-index={i} className={`bg-white border-border/50 shadow-sm hover:shadow-md hover:scale-[1.04] hover:border-primary/20 transition-all duration-300 group rounded-md animate-fade-in ${i % 3 === 0 ? "delay-100" : i % 3 === 1 ? "delay-200" : "delay-300"}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center size-8 rounded-md bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <div className="size-2.5 rounded-md bg-primary ring-4 ring-primary/20 group-hover:ring-primary/30 transition-all" />
                        </div>
                        <CardTitle className="font-default text-xl text-foreground">
                          {feature.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground font-default leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>)}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>;
}